import { useState, useEffect, useRef } from 'react';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { FaWrench, FaChevronRight, FaPhone, FaBullhorn, FaCreditCard, FaCalendarAlt, FaTools } from 'react-icons/fa';
import TenantSidebar from './TenantSidebar';
import TenantNavbar from './TenantNavbar';
import LoadingScreen from '../PropertyManager/LoadingScreen';
import '../PropertyManager/Sidebar.css';
import '../PropertyManager/Navbar.css';
import './TenantHome.css';
import Tutorial from '../PropertyManager/Tutorial';

const BASE_URL = 'https://api.rside.app';

const TH_STEPS = [
  {
    title: 'Welcome to Your Home',
    description: 'This is your tenant dashboard — here you\'ll see rent, maintenance, and announcements.',
  },
  {
    title: 'Rent Due',
    description: 'Your current balance and due date are shown here. Tap "Payment history" to see all past payments.',
    anchorId: 'th-rent-card',
    cardAlignment: 'bottom',
  },
  {
    title: 'Maintenance Requests',
    description: 'See your open maintenance requests here. Tap "View all requests" to manage them.',
    anchorId: 'th-requests-card',
    cardAlignment: 'bottom',
  },
  {
    title: 'Announcements',
    description: 'Your property manager\'s latest messages appear here. Tap "View all" to read every announcement in your inbox.',
    anchorId: 'th-ann-card',
    cardAlignment: 'bottom',
  },
  {
    title: 'Your Property Manager',
    description: 'View your property manager\'s contact info and reach out directly from this card.',
    anchorId: 'th-pm-card',
    cardAlignment: 'bottom',
  },
  {
    title: 'Bell Notifications',
    description: 'A red dot appears on the bell when you have new announcements. Tap it anytime to open your full announcement feed.',
    anchorId: 'th-bell-btn',
    cardAlignment: 'bottom',
  },
];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function nextDueDateObj(rentDueDay) {
  const today = new Date();
  const day = parseInt(rentDueDay) || 1;
  let due = new Date(today.getFullYear(), today.getMonth(), day);
  if (due <= today) due = new Date(today.getFullYear(), today.getMonth() + 1, day);
  return due;
}

function daysUntilNextDue(rentDueDay) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((nextDueDateObj(rentDueDay) - today) / 86400000);
}

function daysPastCurrentDue(rentDueDay) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = parseInt(rentDueDay) || 1;
  const currentDue = new Date(today.getFullYear(), today.getMonth(), day);
  return Math.round((today - currentDue) / 86400000);
}

function fmtDueDate(date, short = false) {
  return date.toLocaleDateString('en-US', short
    ? { month: 'short', day: 'numeric' }
    : { month: 'long', day: 'numeric', year: 'numeric' }
  );
}

function fmt$(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function annIsNew(ann) {
  const created = ann.created_at || ann.Created_At || ann.date_created;
  if (!created) return false;
  return (Date.now() - new Date(created).getTime()) < 7 * 24 * 60 * 60 * 1000;
}

function TenantHome() {
  const navigate = useNavigate();
  const navbarRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [rentSummary, setRentSummary] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [pendingFees, setPendingFees] = useState([]);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) { navigate('/tenant/login'); return; }
      try {
        const sessionRes = await fetch(`${BASE_URL}/TenantApp/SessionInfo?email=${encodeURIComponent(user.email)}`);
        const sessionData = await sessionRes.json();
        setSession(sessionData);
        setUserEmail(user.email);

        const doorId = sessionData?.tenant?.door_id;
        if (doorId) {
          const [woRes, rentRes, annRes, feesRes] = await Promise.all([
            fetch(`${BASE_URL}/TenantApp/GetWorkOrders?door_id=${doorId}`),
            fetch(`${BASE_URL}/GetPastDuePaymentsByDoor?door_id=${doorId}`),
            fetch(`${BASE_URL}/GetAnnouncementsByDoor?door_id=${doorId}`),
            fetch(`${BASE_URL}/GetPushedFeesByDoor?door_id=${doorId}`),
          ]);
          const woData = await woRes.json();
          const rentData = await rentRes.json();
          const annData = await annRes.json();
          setWorkOrders(woData?.work_orders || []);
          setRentSummary(rentData);
          setAnnouncements(Array.isArray(annData) ? annData : (annData.announcements || annData.Announcements || []));
          if (feesRes.ok) {
            const feesData = await feesRes.json();
            setPendingFees((feesData.pushed_fees || []).filter(f => (f.Status || '').toLowerCase() === 'pending'));
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, [navigate]);

  if (loading) return <LoadingScreen />;

  const tenant = session?.tenant;
  const pm = session?.property_manager;
  const firstName = (tenant?.full_name || '').split(' ')[0] || '';
  const initials = (tenant?.full_name || '').split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);

  const summary = rentSummary?.summary;
  const lease = rentSummary?.lease;
  const monthlyRent = Number(summary?.monthly_rent || lease?.Rent || 0);
  const rentDueDay = lease?.Rent_Due || 1;
  const apiPastDue = Number(summary?.past_due ?? 0);
  const pushedFeesTotal = pendingFees.reduce((s, f) => s + Number(f.Amount || 0), 0);
  const withinThreeDays = daysUntilNextDue(rentDueDay) <= 3;
  const hasRentComponent = apiPastDue > pushedFeesTotal;

  let totalDue;
  if (hasRentComponent) {
    totalDue = apiPastDue;
  } else if (withinThreeDays) {
    totalDue = monthlyRent + pushedFeesTotal;
  } else if (pushedFeesTotal > 0) {
    totalDue = pushedFeesTotal;
  } else {
    totalDue = 0;
  }

  let dueDateStr = null;
  if (totalDue > 0) {
    if (hasRentComponent) {
      const currentDue = new Date(new Date().getFullYear(), new Date().getMonth(), rentDueDay);
      dueDateStr = fmtDueDate(currentDue, true);
    } else {
      dueDateStr = fmtDueDate(nextDueDateObj(rentDueDay), true);
    }
  }

  const pastDays = daysPastCurrentDue(rentDueDay);
  const badgePill = totalDue === 0 ? null
    : (hasRentComponent && pastDays >= 5) ? 'PAST DUE'
    : 'UPCOMING';

  const openWOs = workOrders.filter(wo => (wo.Stat || '').toLowerCase() === 'open');
  const inProgressWOs = workOrders.filter(wo => (wo.Stat || '').toLowerCase() === 'in progress');

  const pmInitials = (pm?.full_name || '').split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="home-page">
      <TenantSidebar initials={initials} />
      <div className="main-content">
        <TenantNavbar ref={navbarRef} title="Home" tenantName={tenant?.full_name || ''} initials={initials} />

        <div className="th-page-body">

          {/* ── Header card: greeting + stat pills ── */}
          <div className="th-header-card">
            <div className="th-header-left">
              <p className="th-greet-sub">{getGreeting()},</p>
              <h1 className="th-greet-name">{firstName}</h1>
            </div>
            <div className="th-header-stats">
              <div className="th-stat-card">
                <div className="th-stat-icon th-stat-icon-blue"><FaCreditCard /></div>
                <div className="th-stat-info">
                  <span className="th-stat-value">{fmt$(monthlyRent)}</span>
                  <span className="th-stat-label">Monthly Rent</span>
                </div>
              </div>
              <div className="th-stat-card">
                <div className="th-stat-icon th-stat-icon-green"><FaCalendarAlt /></div>
                <div className="th-stat-info">
                  <span className="th-stat-value">{lease?.Rent_Due ? `${lease.Rent_Due}${[,'st','nd','rd'][lease.Rent_Due] || 'th'}` : '—'}</span>
                  <span className="th-stat-label">Due Day</span>
                </div>
              </div>
              <div className="th-stat-card">
                <div className={`th-stat-icon ${openWOs.length > 0 ? 'th-stat-icon-orange' : 'th-stat-icon-blue'}`}><FaTools /></div>
                <div className="th-stat-info">
                  <span className="th-stat-value">{openWOs.length}</span>
                  <span className="th-stat-label">Open Requests</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Dashboard 2-col layout ── */}
          <div className="th-dashboard">

            {/* ── LEFT: Rent + Announcement ── */}
            <div className="th-dash-left">

              {/* Rent card */}
              <div id="th-rent-card" className={`th-rent-card ${badgePill === 'PAST DUE' ? 'th-rent-pastdue' : ''}`}>
                <div className="th-rent-top">
                  <span className="th-rent-label">{totalDue === 0 ? 'ALL PAID' : 'BALANCE DUE'}</span>
                  {badgePill && (
                    <span className={badgePill === 'PAST DUE' ? 'th-rent-badge' : 'th-rent-badge th-rent-badge-upcoming'}>
                      {badgePill}
                    </span>
                  )}
                </div>
                <div className="th-rent-amount">{fmt$(totalDue)}</div>
                {dueDateStr && (
                  <div className="th-rent-due-row">
                    <span className="th-rent-due-text">Due {dueDateStr}</span>
                  </div>
                )}
                {pm?.email && (
                  <div className="th-rent-zelle">
                    <span className="th-rent-zelle-icon">💳</span>
                    Pay via Zelle – {pm.email}
                  </div>
                )}
                <button className="th-rent-history-btn" onClick={() => navigate('/tenant/payment-history')}>
                  <span>Payment history</span>
                  <FaChevronRight style={{ fontSize: 11 }} />
                </button>
              </div>

              {/* Announcement card */}
              {(() => {
                const latest = announcements[0];
                const newCount = announcements.filter(annIsNew).length;
                return (
                  <div id="th-ann-card" className="th-section-card th-announcement-card">
                    <div className="th-section-hd">
                      <div className="th-announce-label-row">
                        <FaBullhorn className="th-announce-icon" />
                        <span className="th-section-label">ANNOUNCEMENTS</span>
                        {announcements.length > 0 && (
                          <span className="th-ann-count-pill">{announcements.length}</span>
                        )}
                      </div>
                      {newCount > 0 && <span className="th-new-badge">NEW</span>}
                    </div>
                    {latest ? (
                      <div className="th-announce-body">
                        <p className="th-announce-title">{latest.title || latest.Title || 'Announcement'}</p>
                        <p className="th-announce-preview">
                          {(latest.message || latest.Message || '').slice(0, 140)}
                          {(latest.message || latest.Message || '').length > 140 ? '…' : ''}
                        </p>
                        <button className="th-view-all-btn" onClick={() => navbarRef.current?.openAnnouncements()}>
                          View all <FaChevronRight style={{ fontSize: 10, marginLeft: 3 }} />
                        </button>
                      </div>
                    ) : (
                      <p className="th-empty-text">No announcements right now</p>
                    )}
                  </div>
                );
              })()}

            </div>

            {/* ── RIGHT: Requests + PM ── */}
            <div className="th-dash-right">

              {/* Requests card */}
              <div id="th-requests-card" className="th-section-card">
                <div className="th-section-hd">
                  <span className="th-section-label">MAINTENANCE</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {openWOs.length > 0 && <span className="th-open-badge">{openWOs.length} open</span>}
                    {inProgressWOs.length > 0 && <span className="th-inprog-badge">{inProgressWOs.length} in progress</span>}
                  </div>
                </div>
                {openWOs.length === 0 ? (
                  <p className="th-empty-text">No open requests</p>
                ) : (
                  <div className="th-wo-list">
                    {openWOs.slice(0, 4).map(wo => (
                      <div key={wo.id} className="th-wo-row">
                        <div className="th-wo-icon"><FaWrench /></div>
                        <span className="th-wo-title">{wo.Title || 'Untitled'}</span>
                        <span className="th-wo-status">Open</span>
                      </div>
                    ))}
                  </div>
                )}
                <button className="th-view-all-btn" onClick={() => navigate('/tenant/maintenance')}>
                  View all requests <FaChevronRight style={{ fontSize: 10, marginLeft: 3 }} />
                </button>
              </div>

              {/* PM card */}
              <div id="th-pm-card" className="th-section-card">
                <div className="th-section-hd">
                  <span className="th-section-label">PROPERTY MANAGER</span>
                </div>
                <div className="th-pm-row">
                  <div className="th-pm-avatar">{pmInitials || '?'}</div>
                  <div className="th-pm-info">
                    <span className="th-pm-name">{pm?.full_name || '—'}</span>
                    <span className="th-pm-email">{pm?.email || ''}</span>
                  </div>
                  {pm?.phone_number && (
                    <a href={`tel:${pm.phone_number}`} className="th-pm-call">
                      <FaPhone />
                    </a>
                  )}
                </div>
                {pm?.email && (
                  <button className="th-pm-pay-btn" onClick={() => navigate('/tenant/payments')}>
                    Pay Rent
                  </button>
                )}
              </div>

            </div>

          </div>

        </div>
      </div>
      <Tutorial
        steps={TH_STEPS}
        storageKey={`hasSeenTenantHomeTutorial_${userEmail}`}
      />
    </div>
  );
}

export default TenantHome;
